import React, { useState } from 'react';
import {
  Plus, Search, Edit2, Trash2, Cloud, AlertTriangle,
  CheckCircle, Clock, Cpu, MemoryStick, HardDrive,
  Copy, Eye, Play, Square, History, RefreshCw, Database, Layers,
  Download, FileText
} from 'lucide-react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import { VirtualServer } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { showGlobalNotification } from '../hooks/useNotifications';
import { apiService } from '../services/api';
import Modal from './Modal';
import { useGlobalSearch } from '../hooks/useGlobalSearch';
import { useLanguage } from '../contexts/LanguageContext';
import ServerUpdateCheckModal from './ServerUpdateCheckModal';

const VirtualServersPage: React.FC = () => {
  useAuth();
  const { t } = useLanguage();
  const [searchTerm, setSearchTerm] = useState('');
  useGlobalSearch('virtual-servers', setSearchTerm);
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [editingVM, setEditingVM] = useState<VirtualServer | null>(null);
  const [selectedVM, setSelectedVM] = useState<VirtualServer | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterPhysicalServer, setFilterPhysicalServer] = useState<number>(0);
  const [filterCompany, setFilterCompany] = useState<number>(0);
  const [filterLocation, setFilterLocation] = useState<number>(0);
  const [filterStorageUnit, setFilterStorageUnit] = useState<number>(0);
  const [filterStoragePool, setFilterStoragePool] = useState<number>(0);
  const [filterHypervisor, setFilterHypervisor] = useState<string>('all');
  const [vmsData, setVmsData] = useState<VirtualServer[]>([]);
  const [physicalServers, setPhysicalServers] = useState<any[]>([]);
  const [companies, setCompanies] = useState<any[]>([]);
  const [locations, setLocations] = useState<any[]>([]);
  const [allUsers, setAllUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [storageUnits, setStorageUnits] = useState<any[]>([]);
  const [storagePools, setStoragePools] = useState<any[]>([]);
  const [vmActivities, setVmActivities] = useState<any[]>([]);
  const [loadingActivities, setLoadingActivities] = useState(false);
  const [showUpdateCheckModal, setShowUpdateCheckModal] = useState(false);
  const [updateCheckVM, setUpdateCheckVM] = useState<VirtualServer | null>(null);

  // Load data on component mount
  React.useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [vmsResponse, physicalResponse, companiesResponse, locationsResponse, usersResponse, storageResponse] = await Promise.all([
        apiService.getVirtualServers(),
        apiService.getPhysicalServers(),
        apiService.getCompanies(),
        apiService.getLocations(),
        apiService.getUsers(),
        apiService.getStorageUnits()
      ]);

      setVmsData(Array.isArray(vmsResponse) ? vmsResponse : []);
      setPhysicalServers(Array.isArray(physicalResponse) ? physicalResponse : []);
      setCompanies(Array.isArray(companiesResponse) ? companiesResponse : []);
      setLocations(Array.isArray(locationsResponse) ? locationsResponse : []);
      setAllUsers(Array.isArray(usersResponse) ? usersResponse : []);
      const storageList = storageResponse?.data ?? (Array.isArray(storageResponse) ? storageResponse : []);
      setStorageUnits(storageList);
    } catch (error) {
      console.error('Error loading data:', error);
      showGlobalNotification.error(t('common.error'), t('virtserver.loadError'));
    } finally {
      setLoading(false);
    }
  };
  
  const [vmData, setVmData] = useState({
    vm_name: '',
    hostname: '',
    ip_address: '',
    physical_server_id: 0,
    vcpu_count: 2,
    ram_gb: 4,
    storage_gb: 100,
    operating_system: '',
    hypervisor: 'vmware' as VirtualServer['hypervisor'],
    company_id: 0,
    location_id: 0,
    purpose: '',
    status: 'stopped' as VirtualServer['status'],
    assigned_admin_id: 0,
    auto_start: false,
    backup_schedule: '',
    notes: '',
    storage_unit_id: 0,
    storage_pool_id: 0
  });

  const statusOptions = [
    { value: 'running', label: t('virtserver.statusRunning'), color: 'bg-green-100 text-green-800' },
    { value: 'stopped', label: t('virtserver.statusStopped'), color: 'bg-red-100 text-red-800' },
    { value: 'suspended', label: t('virtserver.statusSuspended'), color: 'bg-yellow-100 text-yellow-800' },
    { value: 'maintenance', label: t('virtserver.statusMaintenance'), color: 'bg-orange-100 text-orange-800' }
  ];

  const hypervisorOptions = [
    { value: 'vmware', label: 'VMware vSphere' },
    { value: 'hyper-v', label: 'Microsoft Hyper-V' },
    { value: 'kvm', label: 'KVM' },
    { value: 'xen', label: 'Citrix Xen' }
  ];


  const uniqueStorageUnitsInData = Array.from(
    new Map(vmsData.filter(vm => vm.storage_unit_id).map(vm => [vm.storage_unit_id, { id: vm.storage_unit_id, name: (vm as any).storage_unit_name }])).values()
  );
  const uniqueStoragePoolsInData = Array.from(
    new Map(vmsData.filter(vm => vm.storage_pool_id && (filterStorageUnit === 0 || vm.storage_unit_id === filterStorageUnit)).map(vm => [vm.storage_pool_id, { id: vm.storage_pool_id, name: (vm as any).storage_pool_name }])).values()
  );

  const filteredVMs = vmsData.filter(vm => {
    const matchesSearch = String(vm.vm_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         String(vm.hostname || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         vm.ip_address.includes(searchTerm) ||
                         String(vm.purpose || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || vm.status === filterStatus;
    const matchesPhysicalServer = filterPhysicalServer === 0 || vm.physical_server_id === filterPhysicalServer;
    const matchesCompany = filterCompany === 0 || vm.company_id === filterCompany;
    const matchesLocation = filterLocation === 0 || vm.location_id === filterLocation;
    const matchesStorageUnit = filterStorageUnit === 0 || vm.storage_unit_id === filterStorageUnit;
    const matchesStoragePool = filterStoragePool === 0 || vm.storage_pool_id === filterStoragePool;
    const matchesHypervisor = filterHypervisor === 'all' || vm.hypervisor === filterHypervisor;

    return matchesSearch && matchesStatus && matchesPhysicalServer && matchesCompany &&
           matchesLocation && matchesStorageUnit && matchesStoragePool && matchesHypervisor;
  });

  const handleEdit = async (vm: VirtualServer) => {
    setEditingVM(vm);
    setVmData({
      vm_name: vm.vm_name,
      hostname: vm.hostname,
      ip_address: vm.ip_address,
      physical_server_id: vm.physical_server_id,
      vcpu_count: vm.vcpu_count,
      ram_gb: vm.ram_gb,
      storage_gb: vm.storage_gb,
      operating_system: vm.operating_system,
      hypervisor: vm.hypervisor,
      company_id: vm.company_id,
      location_id: vm.location_id,
      purpose: vm.purpose,
      status: vm.status,
      assigned_admin_id: vm.assigned_admin_id || 0,
      auto_start: vm.auto_start ?? false,
      backup_schedule: vm.backup_schedule || '',
      notes: vm.notes || '',
      storage_unit_id: vm.storage_unit_id || 0,
      storage_pool_id: vm.storage_pool_id || 0
    });
    if (vm.storage_unit_id) {
      try {
        const pools = await apiService.getStoragePools(vm.storage_unit_id);
        setStoragePools(Array.isArray(pools) ? pools : []);
      } catch {
        setStoragePools([]);
      }
    } else {
      setStoragePools([]);
    }
    setShowModal(true);
  };

  const handleDuplicate = (vm: VirtualServer) => {
    setEditingVM(null);
    setVmData({
      vm_name: `${vm.vm_name} ${t('virtserver.copyNameSuffix')}`,
      hostname: '',
      ip_address: '',
      physical_server_id: vm.physical_server_id,
      vcpu_count: vm.vcpu_count,
      ram_gb: vm.ram_gb,
      storage_gb: vm.storage_gb,
      operating_system: vm.operating_system,
      hypervisor: vm.hypervisor,
      company_id: vm.company_id,
      location_id: vm.location_id,
      purpose: vm.purpose,
      status: 'stopped',
      assigned_admin_id: vm.assigned_admin_id || 0,
      auto_start: vm.auto_start ?? false,
      backup_schedule: vm.backup_schedule || '',
      notes: vm.notes || '',
      storage_unit_id: vm.storage_unit_id || 0,
      storage_pool_id: vm.storage_pool_id || 0
    });
    setStoragePools([]);
    setShowModal(true);
  };

  const handleAdd = () => {
    setEditingVM(null);
    setVmData({
      vm_name: '',
      hostname: '',
      ip_address: '',
      physical_server_id: 0,
      vcpu_count: 2,
      ram_gb: 4,
      storage_gb: 100,
      operating_system: '',
      hypervisor: 'vmware',
      company_id: 0,
      location_id: 0,
      purpose: '',
      status: 'stopped',
      assigned_admin_id: 0,
      auto_start: false,
      backup_schedule: '',
      notes: '',
      storage_unit_id: 0,
      storage_pool_id: 0
    });
    setStoragePools([]);
    setShowModal(true);
  };

  const handleStorageUnitChange = async (storageId: number) => {
    setVmData(prev => ({ ...prev, storage_unit_id: storageId, storage_pool_id: 0 }));
    if (storageId) {
      try {
        const pools = await apiService.getStoragePools(storageId);
        setStoragePools(Array.isArray(pools) ? pools : []);
      } catch {
        setStoragePools([]);
      }
    } else {
      setStoragePools([]);
    }
  };

  const validateIPAddress = (ip: string): boolean => {
    const ipRegex = /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
    return ipRegex.test(ip);
  };

  const handleSave = async () => {
    if (!vmData.vm_name || !vmData.hostname || !vmData.ip_address || !vmData.physical_server_id) {
      showGlobalNotification.error(t('common.error'), t('virtserver.errorRequired'));
      return;
    }

    // IP adresi geçerlilik kontrolü
    if (!validateIPAddress(vmData.ip_address)) {
      showGlobalNotification.error(t('common.error'), t('virtserver.errorInvalidIp'));
      return;
    }

    // Hostname duplicate kontrolü (düzenleme yapılıyorsa kendi kaydını hariç tut)
    const duplicateHostname = vmsData.find(vm => {
      // Eğer düzenleme modundaysak ve bu kayıt düzenlenen kayıtsa, atla
      if (editingVM && vm.id === editingVM.id) {
        return false;
      }
      // Hostname karşılaştır
      return vm.hostname.toLowerCase() === vmData.hostname.toLowerCase();
    });
    if (duplicateHostname) {
      showGlobalNotification.error(t('common.error'), t('virtserver.errorDuplicateHostname', { name: duplicateHostname.vm_name }));
      return;
    }

    // IP adresi duplicate kontrolü (düzenleme yapılıyorsa kendi kaydını hariç tut)
    const duplicateIP = vmsData.find(vm => {
      // Eğer düzenleme modundaysak ve bu kayıt düzenlenen kayıtsa, atla
      if (editingVM && vm.id === editingVM.id) {
        return false;
      }
      // IP karşılaştır
      return vm.ip_address === vmData.ip_address;
    });
    if (duplicateIP) {
      showGlobalNotification.error(t('common.error'), t('virtserver.errorDuplicateIp', { name: duplicateIP.vm_name, ip: duplicateIP.ip_address }));
      return;
    }

    try {
      // Ensure auto_start is always boolean
      const dataToSend = {
        ...vmData,
        auto_start: Boolean(vmData.auto_start),
        backup_schedule: vmData.backup_schedule || '',
        storage_unit_id: vmData.storage_unit_id || null,
        storage_pool_id: vmData.storage_pool_id || null
      };

      console.log('Sending data:', dataToSend);
      console.log('auto_start type:', typeof dataToSend.auto_start, 'value:', dataToSend.auto_start);

      if (editingVM) {
        await apiService.updateVirtualServer(editingVM.id, dataToSend);
        showGlobalNotification.success(t('common.success'), t('virtserver.successUpdated'));
      } else {
        await apiService.createVirtualServer(dataToSend);
        showGlobalNotification.success(t('common.success'), t('virtserver.successCreated'));
      }

      await loadData();
      setShowModal(false);
      setStoragePools([]);
      setVmData({
        vm_name: '',
        hostname: '',
        ip_address: '',
        physical_server_id: 0,
        vcpu_count: 2,
        ram_gb: 4,
        storage_gb: 100,
        operating_system: '',
        hypervisor: 'vmware',
        company_id: 0,
        location_id: 0,
        purpose: '',
        status: 'stopped',
        assigned_admin_id: 0,
        auto_start: false,
        backup_schedule: '',
        notes: '',
        storage_unit_id: 0,
        storage_pool_id: 0
      });
    } catch (error: any) {
      console.error('Error saving virtual server:', error);
      showGlobalNotification.error(t('common.error'), error.message || t('virtserver.errorSave'));
    }
  };


  const handleDelete = async (id: number) => {
    const vmToDelete = vmsData.find(v => v.id === id);
    if (!vmToDelete) return;
    
    if (confirm(t('virtserver.confirmDelete'))) {
      try {
        await apiService.deleteVirtualServer(id);
        await loadData();
        showGlobalNotification.success(t('common.success'), t('virtserver.successDeleted'));
      } catch (error: any) {
        console.error('Error deleting virtual server:', error);
        showGlobalNotification.error(t('common.error'), error.message || t('virtserver.errorDelete'));
      }
    }
  };

  const showVMDetail = (vm: VirtualServer) => {
    setSelectedVM(vm);
    setShowDetailModal(true);
  };

  const showUpdateCheck = (vm: VirtualServer) => {
    setUpdateCheckVM(vm);
    setShowUpdateCheckModal(true);
  };

  const showVMHistory = async (vm: VirtualServer) => {
    setSelectedVM(vm);
    setShowHistoryModal(true);
    setLoadingActivities(true);
    try {
      const activities = await apiService.getVMActivities(vm.id);
      setVmActivities(Array.isArray(activities) ? activities : []);
    } catch (error) {
      console.error('Error loading VM activities:', error);
      showGlobalNotification.error(t('common.error'), t('virtserver.errorLoadActivities'));
      setVmActivities([]);
    } finally {
      setLoadingActivities(false);
    }
  };

  const exportToExcel = () => {
    try {
      const locale = 'tr-TR';
      const exportData = filteredVMs.map((vm, i) => ({
        '#': i + 1,
        'VM Adı': vm.vm_name,
        'Hostname': vm.hostname,
        'IP Adresi': vm.ip_address,
        'Fiziksel Sunucu': (vm as any).physical_server_name || '',
        'Şirket': (vm as any).company_name || '',
        'Konum': (vm as any).location_name || '',
        'İşletim Sistemi': vm.operating_system,
        'Hypervisor': vm.hypervisor,
        'vCPU': vm.vcpu_count,
        'RAM (GB)': vm.ram_gb,
        'Disk (GB)': vm.storage_gb,
        'Storage Ünitesi': (vm as any).storage_unit_name || '',
        'Storage Pool': (vm as any).storage_pool_name || '',
        'Amaç': vm.purpose,
        'Durum': statusOptions.find(s => s.value === vm.status)?.label || vm.status,
        'Sorumlu Admin': (vm as any).admin_name || '',
        'Yedekleme Planı': vm.backup_schedule || '',
        'Oluşturma Tarihi': vm.created_at ? new Date(vm.created_at).toLocaleDateString(locale) : '',
      }));
      const ws = XLSX.utils.json_to_sheet(exportData);
      ws['!cols'] = [
        {wch:5},{wch:20},{wch:18},{wch:16},{wch:20},{wch:18},{wch:16},
        {wch:20},{wch:12},{wch:8},{wch:10},{wch:10},{wch:20},{wch:18},
        {wch:16},{wch:12},{wch:18},{wch:18},{wch:14}
      ];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, t('virtserver.excelSheet'));
      XLSX.writeFile(wb, `${t('virtserver.excelFilename')}-${new Date().toISOString().split('T')[0]}.xlsx`);
      showGlobalNotification.success(t('common.success'), `${filteredVMs.length} ${t('virtserver.exportExcelSuccess')}`);
    } catch (err) {
      showGlobalNotification.error(t('common.error'), t('virtserver.exportExcelError'));
    }
  };

  const exportToPDF = () => {
    try {
      const locale = 'tr-TR';
      const pdf = new jsPDF('l', 'mm', 'a4');
      pdf.setFontSize(16);
      pdf.text(t('virtserver.pdfTitle'), 14, 15);
      pdf.setFontSize(10);
      pdf.text(`${t('virtserver.pdfReportDate')} ${new Date().toLocaleDateString(locale)}`, 14, 23);
      pdf.text(`${t('virtserver.pdfTotal')} ${filteredVMs.length}`, 200, 23);
      (pdf as any).autoTable({
        head: [['#', 'VM Adı', 'Hostname', 'IP Adresi', 'Fiz. Sunucu', 'Şirket', 'İşl. Sistemi', 'Hypervisor', 'vCPU', 'RAM', 'Disk', 'Storage', 'Pool', 'Durum']],
        body: filteredVMs.map((vm, i) => [
          i + 1,
          vm.vm_name,
          vm.hostname,
          vm.ip_address,
          (vm as any).physical_server_name || '-',
          (vm as any).company_name || '-',
          vm.operating_system,
          vm.hypervisor,
          vm.vcpu_count,
          `${vm.ram_gb} GB`,
          `${vm.storage_gb} GB`,
          (vm as any).storage_unit_name || '-',
          (vm as any).storage_pool_name || '-',
          statusOptions.find(s => s.value === vm.status)?.label || vm.status,
        ]),
        startY: 28,
        styles: { fontSize: 6.5, cellPadding: 1.5 },
        headStyles: { fillColor: [124, 58, 237], textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [245, 247, 250] },
        margin: { top: 28, left: 10, right: 10 },
      });
      pdf.save(`${t('virtserver.excelFilename')}-${new Date().toISOString().split('T')[0]}.pdf`);
      showGlobalNotification.success(t('common.success'), `${filteredVMs.length} ${t('virtserver.exportPdfSuccess')}`);
    } catch (err) {
      showGlobalNotification.error(t('common.error'), t('virtserver.exportPdfError'));
    }
  };

  const getStatusColor = (status: string) => {
    return statusOptions.find(s => s.value === status)?.color || 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
  };

  const getHealthColor = (health: string) => {
    return healthOptions.find(h => h.value === health)?.color || 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
  };

  const getHealthIcon = (health: string) => {
    switch (health) {
      case 'healthy':
        return CheckCircle;
      case 'warning':
        return AlertTriangle;
      case 'critical':
        return AlertTriangle;
      default:
        return Clock;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'running':
        return CheckCircle;
      case 'stopped':
        return Clock;
      case 'suspended':
        return Clock;
      default:
        return Clock;
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
          <span className="ml-2 text-gray-600 dark:text-gray-400">{t('virtserver.loading')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">{t('virtserver.title')}</h1>
          <p className="text-gray-600 dark:text-gray-400 text-sm sm:text-base">{t('virtserver.subtitle')}</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <button onClick={exportToExcel} className="bg-green-600 text-white px-3 sm:px-4 py-2.5 rounded-lg flex items-center justify-center gap-2 hover:bg-green-700 text-sm min-h-[44px]">
            <Download className="w-4 h-4" />
            <span className="hidden sm:inline">Excel</span>
          </button>
          <button onClick={exportToPDF} className="bg-red-600 text-white px-3 sm:px-4 py-2.5 rounded-lg flex items-center justify-center gap-2 hover:bg-red-700 text-sm min-h-[44px]">
            <FileText className="w-4 h-4" />
            <span className="hidden sm:inline">PDF</span>
          </button>
          <button
            onClick={handleAdd}
            className="flex items-center justify-center px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors min-h-[44px] text-sm sm:text-base w-full sm:w-auto"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('virtserver.addNew')}
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6 mb-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">{t('virtserver.totalVM')}</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{vmsData.length}</p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <Cloud className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">{t('virtserver.running')}</p>
              <p className="text-2xl font-bold text-green-600">
                {vmsData.filter(vm => vm.status === 'running').length}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <Play className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">{t('virtserver.stoppedStat')}</p>
              <p className="text-2xl font-bold text-red-600">
                {vmsData.filter(vm => vm.status === 'stopped').length}
              </p>
            </div>
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
              <Square className="w-6 h-6 text-red-600" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">{t('virtserver.totalVcpu')}</p>
              <p className="text-2xl font-bold text-primary">
                {vmsData.reduce((sum, vm) => sum + vm.vcpu_count, 0)}
              </p>
            </div>
            <div className="w-12 h-12 bg-primary/20 rounded-lg flex items-center justify-center">
              <Cpu className="w-6 h-6 text-primary" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 mb-6 space-y-3">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder={t('virtserver.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent w-full bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            />
          </div>
          <select value={filterPhysicalServer} onChange={(e) => setFilterPhysicalServer(parseInt(e.target.value))} className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100">
            <option value={0}>{t('virtserver.allPhysicalServers')}</option>
            {physicalServers.map(server => (
              <option key={server.id} value={server.id}>{server.server_name}</option>
            ))}
          </select>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100">
            <option value="all">{t('virtserver.allStatuses')}</option>
            {statusOptions.map(status => (
              <option key={status.value} value={status.value}>{status.label}</option>
            ))}
          </select>
          <select value={filterHypervisor} onChange={(e) => setFilterHypervisor(e.target.value)} className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100">
            <option value="all">{t('virtserver.allHypervisors')}</option>
            {hypervisorOptions.map(h => (
              <option key={h.value} value={h.value}>{h.label}</option>
            ))}
          </select>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <select value={filterCompany} onChange={(e) => setFilterCompany(parseInt(e.target.value))} className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100">
            <option value={0}>{t('virtserver.allCompanies')}</option>
            {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select value={filterLocation} onChange={(e) => setFilterLocation(parseInt(e.target.value))} className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100">
            <option value={0}>{t('virtserver.allLocations')}</option>
            {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
          <select value={filterStorageUnit} onChange={(e) => { setFilterStorageUnit(parseInt(e.target.value)); setFilterStoragePool(0); }} className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100">
            <option value={0}>{t('virtserver.allStorageUnits')}</option>
            {uniqueStorageUnitsInData.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
          <select value={filterStoragePool} onChange={(e) => setFilterStoragePool(parseInt(e.target.value))} disabled={uniqueStoragePoolsInData.length === 0} className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 disabled:opacity-50">
            <option value={0}>{t('virtserver.allStoragePools')}</option>
            {uniqueStoragePoolsInData.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
      </div>

      {/* Virtual Servers Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('virtserver.colVmName')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('virtserver.colHostname')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('virtserver.colIpAddress')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('virtserver.colPhysicalServer')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('virtserver.colCompany')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('virtserver.colResources')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('virtserver.colStorage')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('virtserver.colStatus')}
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('virtserver.colActions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {filteredVMs.map((vm) => {
                const StatusIcon = getStatusIcon(vm.status);
                return (
                  <tr key={vm.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 bg-purple-100 rounded-lg flex items-center justify-center">
                          <Cloud className="h-5 w-5 text-purple-600" />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{vm.vm_name}</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">{vm.operating_system}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-gray-100">{vm.hostname}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-gray-100 font-mono">{vm.ip_address}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-gray-100">{vm.physical_server_name || '-'}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{vm.location_name || '-'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-gray-100">{vm.company_name || '-'}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{vm.admin_name || t('virtserver.notAssigned')}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-xs text-gray-600 dark:text-gray-400">
                        <div className="flex items-center mb-1">
                          <Cpu className="w-3 h-3 mr-1" />
                          {vm.vcpu_count} vCPU
                        </div>
                        <div className="flex items-center mb-1">
                          <MemoryStick className="w-3 h-3 mr-1" />
                          {vm.ram_gb} GB RAM
                        </div>
                        <div className="flex items-center">
                          <HardDrive className="w-3 h-3 mr-1" />
                          {vm.storage_gb} GB
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {vm.storage_unit_name ? (
                        <div className="text-xs text-gray-700 dark:text-gray-300">
                          <div className="flex items-center mb-1">
                            <Database className="w-3 h-3 mr-1 text-teal-500 flex-shrink-0" />
                            <span className="truncate max-w-[120px]" title={vm.storage_unit_name}>{vm.storage_unit_name}</span>
                          </div>
                          {vm.storage_pool_name && (
                            <div className="flex items-center text-gray-500 dark:text-gray-400">
                              <Layers className="w-3 h-3 mr-1 flex-shrink-0" />
                              <span className="truncate max-w-[120px]" title={vm.storage_pool_name}>{vm.storage_pool_name}</span>
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400 dark:text-gray-500">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(vm.status)}`}>
                        <StatusIcon className="w-3 h-3 mr-1" />
                        {statusOptions.find(s => s.value === vm.status)?.label}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => showUpdateCheck(vm)}
                          className="text-gray-400 hover:text-orange-500 dark:hover:text-orange-400 transition-colors"
                          title="Güncelleme Kontrolü"
                        >
                          <RefreshCw className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => showVMHistory(vm)}
                          className="text-gray-400 hover:text-purple-600 transition-colors"
                          title={t('virtserver.titleHistory')}
                        >
                          <History className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => showVMDetail(vm)}
                          className="text-gray-400 hover:text-primary transition-colors"
                          title={t('virtserver.titleViewDetail')}
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDuplicate(vm)}
                          className="text-gray-400 hover:text-green-600 transition-colors"
                          title={t('virtserver.titleCopy')}
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleEdit(vm)}
                          className="text-gray-400 hover:text-primary transition-colors"
                          title={t('virtserver.titleEdit')}
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(vm.id)}
                          className="text-gray-400 hover:text-red-600 transition-colors"
                          title={t('virtserver.titleDelete')}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {filteredVMs.length === 0 && (
          <div className="text-center py-12">
            <Cloud className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">{t('virtserver.noResults')}</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{t('virtserver.noResultsDesc')}</p>
          </div>
        )}
      </div>

      {/* VM Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-4xl max-h-[95vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {editingVM ? t('virtserver.modalEditTitle') : t('virtserver.modalAddTitle')}
              </h2>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Basic Information */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">{t('virtserver.sectionBasicInfo')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('virtserver.labelVmName')}
                    </label>
                    <input
                      type="text"
                      value={vmData.vm_name}
                      onChange={(e) => setVmData({...vmData, vm_name: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="WEB-APP-01"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('virtserver.labelHostname')}
                    </label>
                    <input
                      type="text"
                      value={vmData.hostname}
                      onChange={(e) => setVmData({...vmData, hostname: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="webapp01.company.local"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('virtserver.labelIpAddress')}
                    </label>
                    <input
                      type="text"
                      value={vmData.ip_address}
                      onChange={(e) => setVmData({...vmData, ip_address: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="192.168.1.50"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('virtserver.labelPhysicalServer')}
                    </label>
                    <select
                      value={vmData.physical_server_id}
                      onChange={(e) => setVmData({...vmData, physical_server_id: parseInt(e.target.value)})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value={0}>{t('virtserver.selectPhysicalServer')}</option>
                      {physicalServers.filter(s => s.status === 'active').map((server) => (
                        <option key={server.id} value={server.id}>
                          {server.server_name} ({server.hostname})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('virtserver.labelOs')}
                    </label>
                    <input
                      type="text"
                      value={vmData.operating_system}
                      onChange={(e) => setVmData({...vmData, operating_system: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="Ubuntu Server 22.04"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('virtserver.labelHypervisor')}
                    </label>
                    <select
                      value={vmData.hypervisor}
                      onChange={(e) => setVmData({...vmData, hypervisor: e.target.value as VirtualServer['hypervisor']})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      {hypervisorOptions.map((hypervisor) => (
                        <option key={hypervisor.value} value={hypervisor.value}>
                          {hypervisor.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('virtserver.labelPurpose')}
                    </label>
                    <input
                      type="text"
                      value={vmData.purpose}
                      onChange={(e) => setVmData({...vmData, purpose: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="Web Application Server"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('virtserver.labelStatus')}
                    </label>
                    <select
                      value={vmData.status}
                      onChange={(e) => setVmData({...vmData, status: e.target.value as VirtualServer['status']})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      {statusOptions.map((status) => (
                        <option key={status.value} value={status.value}>
                          {status.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Resource Allocation */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">{t('virtserver.sectionResources')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('virtserver.labelVcpu')}
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="32"
                      value={vmData.vcpu_count}
                      onChange={(e) => setVmData({...vmData, vcpu_count: parseInt(e.target.value) || 1})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('virtserver.labelRam')}
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="256"
                      value={vmData.ram_gb}
                      onChange={(e) => setVmData({...vmData, ram_gb: parseInt(e.target.value) || 1})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('virtserver.labelStorage')}
                    </label>
                    <input
                      type="number"
                      min="10"
                      value={vmData.storage_gb}
                      onChange={(e) => setVmData({...vmData, storage_gb: parseInt(e.target.value) || 10})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              {/* Location and Assignment */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">{t('virtserver.sectionLocation')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('virtserver.labelCompany')}
                    </label>
                    <select
                      value={vmData.company_id}
                      onChange={(e) => setVmData({...vmData, company_id: parseInt(e.target.value)})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value={0}>{t('virtserver.selectCompany')}</option>
                      {companies.map((company) => (
                        <option key={company.id} value={company.id}>
                          {company.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('virtserver.labelLocation')}
                    </label>
                    <select
                      value={vmData.location_id}
                      onChange={(e) => setVmData({...vmData, location_id: parseInt(e.target.value)})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value={0}>{t('virtserver.selectLocation')}</option>
                      {locations.map((location) => (
                        <option key={location.id} value={location.id}>
                          {location.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('virtserver.labelAdmin')}
                    </label>
                    <select
                      value={vmData.assigned_admin_id}
                      onChange={(e) => setVmData({...vmData, assigned_admin_id: parseInt(e.target.value)})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value={0}>{t('virtserver.selectAdmin')}</option>
                      {allUsers.filter(u => u.role === 'admin' || u.role === 'superadmin').map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Storage Assignment */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">{t('virtserver.sectionStorage')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('virtserver.labelStorageUnit')}
                    </label>
                    <select
                      value={vmData.storage_unit_id}
                      onChange={(e) => handleStorageUnitChange(parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value={0}>{t('virtserver.selectStorageUnit')}</option>
                      {storageUnits.map((unit: any) => (
                        <option key={unit.id} value={unit.id}>
                          {unit.storage_name} ({unit.total_capacity_tb} TB)
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('virtserver.labelStoragePool')}
                    </label>
                    <select
                      value={vmData.storage_pool_id}
                      onChange={(e) => setVmData({...vmData, storage_pool_id: parseInt(e.target.value)})}
                      disabled={!vmData.storage_unit_id || storagePools.length === 0}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:opacity-50"
                    >
                      <option value={0}>{t('virtserver.selectStoragePool')}</option>
                      {storagePools.map((pool: any) => {
                        const isCurrentPool = editingVM && editingVM.storage_pool_id === pool.id;
                        const returnedTb = isCurrentPool ? (editingVM.storage_gb / 1024) : 0;
                        const availableTb = Math.max(0, pool.total_capacity_tb - pool.used_capacity_tb + returnedTb);
                        return (
                          <option key={pool.id} value={pool.id}>
                            {pool.pool_name} — {availableTb.toFixed(2)} TB boş / {pool.total_capacity_tb} TB{pool.raid_level ? ` (${pool.raid_level})` : ''}
                          </option>
                        );
                      })}
                    </select>
                    {(() => {
                      if (!vmData.storage_pool_id) return null;
                      const pool = storagePools.find((p: any) => p.id === vmData.storage_pool_id);
                      if (!pool) return null;
                      const isCurrentPool = editingVM && editingVM.storage_pool_id === pool.id;
                      const returnedTb = isCurrentPool ? (editingVM.storage_gb / 1024) : 0;
                      const availableTb = Math.max(0, pool.total_capacity_tb - pool.used_capacity_tb + returnedTb);
                      const requiredTb = vmData.storage_gb / 1024;
                      const insufficient = requiredTb > availableTb;
                      return (
                        <div className={`mt-2 px-3 py-2 rounded-md text-sm flex items-start gap-2 ${insufficient ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300' : 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-300'}`}>
                          {insufficient ? (
                            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                          ) : (
                            <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                          )}
                          <span>
                            {insufficient
                              ? `Yetersiz alan: ${requiredTb.toFixed(2)} TB gerekli, yalnızca ${availableTb.toFixed(2)} TB boş.`
                              : `Yeterli alan: ${availableTb.toFixed(2)} TB boş (${requiredTb.toFixed(2)} TB kullanılacak).`
                            }
                          </span>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('virtserver.labelNotes')}
                </label>
                <textarea
                  value={vmData.notes}
                  onChange={(e) => setVmData({...vmData, notes: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder={t('virtserver.notesPlaceholder')}
                />
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 transition-colors"
              >
                {t('virtserver.btnCancel')}
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
              >
                {editingVM ? t('virtserver.btnUpdate') : t('virtserver.btnSave')}
              </button>
            </div>
          </div>
        </Modal>

      {/* VM Update Check Modal */}
      {updateCheckVM && (
        <ServerUpdateCheckModal
          isOpen={showUpdateCheckModal}
          onClose={() => { setShowUpdateCheckModal(false); setUpdateCheckVM(null); }}
          serverType="virtual"
          serverId={updateCheckVM.id}
          serverName={updateCheckVM.vm_name}
          adminUsers={allUsers.filter(u => u.role === 'admin' || u.role === 'superadmin')}
        />
      )}

      {/* VM Detail Modal */}
      <Modal isOpen={showDetailModal} onClose={() => setShowDetailModal(false)}>
          {selectedVM && (
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {t('virtserver.detailTitle')}
                  </h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    {selectedVM.vm_name} - {selectedVM.hostname}
                  </p>
                </div>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:text-gray-400"
                >
                  ✕
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              {/* VM Overview */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">{t('virtserver.detailVmInfo')}</h3>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('virtserver.detailVmName')}</span>
                      <span className="font-medium">{selectedVM.vm_name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('virtserver.detailHostname')}</span>
                      <span className="font-medium">{selectedVM.hostname}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('virtserver.detailIpAddress')}</span>
                      <span className="font-medium">{selectedVM.ip_address}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('virtserver.detailPhysicalServer')}</span>
                      <span className="font-medium">{selectedVM.physical_server_name || '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('virtserver.detailHypervisor')}</span>
                      <span className="font-medium capitalize">{selectedVM.hypervisor}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('virtserver.detailOs')}</span>
                      <span className="font-medium">{selectedVM.operating_system}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('virtserver.detailPurpose')}</span>
                      <span className="font-medium">{selectedVM.purpose}</span>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">{t('virtserver.detailResources')}</h3>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('virtserver.detailVcpu')}</span>
                      <span className="font-medium">{selectedVM.vcpu_count} Core</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('virtserver.detailRam')}</span>
                      <span className="font-medium">{selectedVM.ram_gb} GB</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('virtserver.detailStorage')}</span>
                      <span className="font-medium">{selectedVM.storage_gb} GB</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('virtserver.detailUptime')}</span>
                      <span className="font-medium">
                        {t('virtserver.detailUptimeDays', { days: Math.floor((new Date().getTime() - new Date(selectedVM.created_at).getTime()) / (1000 * 60 * 60 * 24)) })}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Backup Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">{t('virtserver.detailBackup')}</h3>
                  <div className="space-y-2 text-sm">
                    {selectedVM.backup_schedule && (
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">{t('virtserver.detailBackupSchedule')}</span>
                        <span className="font-medium">{selectedVM.backup_schedule}</span>
                      </div>
                    )}
                    {selectedVM.last_backup && (
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">{t('virtserver.detailLastBackup')}</span>
                        <span className="font-medium">{new Date(selectedVM.last_backup).toLocaleString('tr-TR')}</span>
                      </div>
                    )}
                    {selectedVM.last_snapshot && (
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">{t('virtserver.detailLastSnapshot')}</span>
                        <span className="font-medium">{new Date(selectedVM.last_snapshot).toLocaleString('tr-TR')}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">{t('virtserver.detailSystemInfo')}</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('virtserver.detailCreatedAt')}</span>
                      <span className="font-medium">{new Date(selectedVM.created_at).toLocaleDateString('tr-TR')}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('virtserver.detailUpdatedAt')}</span>
                      <span className="font-medium">{new Date(selectedVM.updated_at).toLocaleDateString('tr-TR')}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('virtserver.detailAdmin')}</span>
                      <span className="font-medium">{selectedVM.admin_name || t('virtserver.notAssigned')}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Storage Info */}
              {(selectedVM.storage_unit_name || selectedVM.storage_pool_name) && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">{t('virtserver.sectionStorage')}</h3>
                  <div className="space-y-2 text-sm">
                    {selectedVM.storage_unit_name && (
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">{t('virtserver.detailStorageUnit')}</span>
                        <span className="font-medium">{selectedVM.storage_unit_name}</span>
                      </div>
                    )}
                    {selectedVM.storage_pool_name && (
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">{t('virtserver.detailStoragePool')}</span>
                        <span className="font-medium">{selectedVM.storage_pool_name}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Notes */}
              {selectedVM.notes && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">{t('virtserver.detailNotes')}</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                    {selectedVM.notes}
                  </p>
                </div>
              )}
            </div>
          </div>
          )}
        </Modal>

      {/* VM History Modal */}
      <Modal isOpen={showHistoryModal} onClose={() => setShowHistoryModal(false)}>
          {selectedVM && (
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {t('virtserver.historyTitle')}
                  </h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    {selectedVM.vm_name} - {selectedVM.hostname}
                  </p>
                </div>
                <button
                  onClick={() => setShowHistoryModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:text-gray-400"
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="p-6">
              {loadingActivities ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                  <span className="ml-2 text-gray-600 dark:text-gray-400">{t('virtserver.historyLoading')}</span>
                </div>
              ) : vmActivities.length === 0 ? (
                <div className="text-center py-12">
                  <History className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">{t('virtserver.historyEmpty')}</h3>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{t('virtserver.historyEmptyDesc')}</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {vmActivities.map((activity, index) => (
                    <div
                      key={activity.id}
                      className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              activity.action_type === 'created' ? 'bg-green-100 text-green-800' :
                              activity.action_type === 'updated' ? 'bg-primary/20 text-primary-dark' :
                              activity.action_type === 'deleted' ? 'bg-red-100 text-red-800' :
                              'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                            }`}>
                              {activity.action_type === 'created' ? t('virtserver.historyActionCreated') :
                               activity.action_type === 'updated' ? t('virtserver.historyActionUpdated') :
                               activity.action_type === 'deleted' ? t('virtserver.historyActionDeleted') :
                               activity.action_type === 'started' ? t('virtserver.historyActionStarted') :
                               activity.action_type === 'stopped' ? t('virtserver.historyActionStopped') :
                               activity.action_type === 'suspended' ? t('virtserver.historyActionSuspended') :
                               activity.action_type}
                            </span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {new Date(activity.created_at).toLocaleString('tr-TR', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </span>
                          </div>
                          <p className="text-sm text-gray-900 dark:text-gray-100 mb-2">{activity.description}</p>
                          <div className="flex items-center space-x-4 text-xs text-gray-600 dark:text-gray-400">
                            <div className="flex items-center">
                              <span className="font-medium mr-1">{t('virtserver.historyPerformedBy')}</span>
                              {activity.performed_by_name || 'Sistem'}
                            </div>
                            {activity.physical_server_name && (
                              <div className="flex items-center">
                                <span className="font-medium mr-1">{t('virtserver.historyPhysicalServer')}</span>
                                {activity.physical_server_name}
                              </div>
                            )}
                          </div>
                          {activity.changed_fields && Object.keys(JSON.parse(activity.changed_fields)).length > 0 && (
                            <div className="mt-2 p-2 bg-gray-50 dark:bg-gray-700 rounded text-xs">
                              <span className="font-medium text-gray-700 dark:text-gray-300">{t('virtserver.historyChangedFields')}</span>
                              <div className="mt-1 space-y-1">
                                {Object.entries(JSON.parse(activity.changed_fields)).map(([field, change]: [string, any]) => (
                                  <div key={field} className="text-gray-600 dark:text-gray-400">
                                    <span className="font-medium">{field}:</span> {change.old} → {change.new}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          {activity.resources && (
                            <div className="mt-2 flex items-center space-x-4 text-xs text-gray-600 dark:text-gray-400">
                              {JSON.parse(activity.resources).vcpu_count && (
                                <div className="flex items-center">
                                  <Cpu className="w-3 h-3 mr-1" />
                                  {JSON.parse(activity.resources).vcpu_count} vCPU
                                </div>
                              )}
                              {JSON.parse(activity.resources).ram_gb && (
                                <div className="flex items-center">
                                  <MemoryStick className="w-3 h-3 mr-1" />
                                  {JSON.parse(activity.resources).ram_gb} GB RAM
                                </div>
                              )}
                              {JSON.parse(activity.resources).storage_gb && (
                                <div className="flex items-center">
                                  <HardDrive className="w-3 h-3 mr-1" />
                                  {JSON.parse(activity.resources).storage_gb} GB
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          )}
        </Modal>
    </div>
  );
};

export default VirtualServersPage;